// frontend/src/pages/ServiceTablePage.js
import React, { useState, useEffect, useRef } from 'react';
import useApi from '../hooks/useApi';
import { Plus, Edit, Trash2, Save, X, Loader2, Upload, Download, FileSpreadsheet, Search } from 'lucide-react';

const ServiceTablePage = () => {
    const { apiFetch, permissions, userProfile } = useApi(); 
    const [services, setServices] = useState([]);
    const [filteredServices, setFilteredServices] = useState([]); 
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Estado do Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingService, setEditingService] = useState(null);
    
    const [formData, setFormData] = useState({ 
        description: '', 
        validity_months: '',
        type: 'EPI', 
        applied_standards: '',
        ansi_standards: '', // NOVO CAMPO: Normas ANSI
        test_performed: ''
    });
    const [saving, setSaving] = useState(false);

    // Referência para o input de arquivo (upload)
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    // Permissões
    const canCreate = permissions?.service_table_create || userProfile?.is_super_user;
    const canEdit = permissions?.service_table_edit || userProfile?.is_super_user;
    const canDelete = permissions?.service_table_delete || userProfile?.is_super_user;

    // Carregar dados
    const fetchServices = async () => {
        setLoading(true);
        try {
            const data = await apiFetch('/service-table');
            setServices(data || []);
            setFilteredServices(data || []);
            setError('');
        } catch (err) {
            console.error("Erro ao buscar serviços:", err);
            setError("Erro ao carregar serviços.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtro de busca local
    useEffect(() => {
        if (!searchTerm) {
            setFilteredServices(services);
        } else {
            const term = searchTerm.toLowerCase();
            const filtered = services.filter(s => 
                s.description.toLowerCase().includes(term) ||
                (s.type && s.type.toLowerCase().includes(term)) ||
                (s.applied_standards && s.applied_standards.toLowerCase().includes(term)) ||
                (s.ansi_standards && s.ansi_standards.toLowerCase().includes(term)) // Inclui busca por ANSI
            );
            setFilteredServices(filtered);
        }
    }, [searchTerm, services]);

    // Handlers do Modal
    const handleOpenModal = (service = null) => {
        setEditingService(service);
        setFormData(service ? { 
            description: service.description, 
            validity_months: service.validity_months,
            type: service.type || 'EPI',
            applied_standards: service.applied_standards || '',
            ansi_standards: service.ansi_standards || '', // Preenche com valor existente
            test_performed: service.test_performed || ''
        } : { 
            description: '', 
            validity_months: '',
            type: 'EPI',
            applied_standards: '',
            ansi_standards: '', // Reset para vazio
            test_performed: ''
        });
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingService(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (editingService) {
                await apiFetch(`/service-table/${editingService.id}`, {
                    method: 'PUT',
                    body: formData
                });
            } else {
                await apiFetch('/service-table', {
                    method: 'POST',
                    body: formData
                });
            }
            handleCloseModal();
            fetchServices(); 
        } catch (err) {
            alert("Erro ao salvar: " + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir este serviço?")) {
            try {
                await apiFetch(`/service-table/${id}`, {
                    method: 'DELETE'
                });
                fetchServices();
            } catch (err) {
                alert("Erro ao excluir: " + err.message);
            }
        }
    };

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token'); 
            const response = await fetch('/api/service-table/export', { 
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) throw new Error('Falha no download');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Tabela_de_Servicos.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (err) {
            alert("Erro ao exportar: " + err.message);
        }
    };

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/service-table/import', { 
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formDataUpload
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Falha na importação');

            alert(result.message);
            fetchServices(); 
        } catch (err) {
            alert("Erro na importação: " + err.message);
        } finally {
            setUploading(false);
            e.target.value = ''; 
        }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Tabela de Serviços</h1>
                    <p className="text-sm text-gray-600">Gerencie os serviços, normas e ensaios aplicáveis.</p>
                </div>
                
                <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                    <div className="relative w-full md:w-64">
                        <input 
                            type="text" 
                            placeholder="Buscar serviço..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                        />
                        <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
                    </div>

                    <button 
                        onClick={handleExport}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
                        title="Baixar planilha"
                    >
                        <Download size={16} /> <span className="hidden sm:inline">Exportar</span>
                    </button>

                    {canCreate && (
                        <>
                            <input 
                                type="file" 
                                ref={fileInputRef} 
                                onChange={handleFileChange} 
                                accept=".xlsx, .xls" 
                                className="hidden" 
                            />
                            <button 
                                onClick={handleImportClick}
                                disabled={uploading}
                                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 disabled:opacity-50 transition-colors"
                                title="Carregar planilha"
                            >
                                {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} 
                                <span className="hidden sm:inline">Importar</span>
                            </button>

                            <button 
                                onClick={() => handleOpenModal()}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 shadow-sm transition-colors"
                            >
                                <Plus size={18} /> Novo Serviço
                            </button>
                        </>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center p-12"><Loader2 className="animate-spin text-blue-600" size={32} /></div>
            ) : error ? (
                <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-md">
                    <p className="font-bold">Erro</p>
                    <p>{error}</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500 font-semibold">
                                <tr>
                                    <th className="px-6 py-3 w-1/4">Descrição do Serviço</th>
                                    <th className="px-4 py-3 w-24">Tipo</th>
                                    <th className="px-4 py-3 w-32">Normas Aplicadas</th>
                                    <th className="px-4 py-3 w-32">Normas ANSI</th> {/* NOVA COLUNA */}
                                    <th className="px-4 py-3 w-48">Ensaio Realizado</th>
                                    <th className="px-4 py-3 text-center w-24">Validade</th>
                                    <th className="px-4 py-3 text-center w-24">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                                {filteredServices.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="p-12 text-center text-gray-400 flex flex-col items-center justify-center">
                                            <FileSpreadsheet className="text-gray-200 mb-3" size={48} />
                                            <p className="text-lg font-medium text-gray-500">Nenhum serviço encontrado.</p>
                                            <p className="text-sm">Tente ajustar a busca ou adicionar um novo serviço.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredServices.map((service) => (
                                        <tr key={service.id} className="hover:bg-blue-50/50 transition-colors group">
                                            <td className="px-6 py-4 font-medium text-gray-900">{service.description}</td>
                                            
                                            <td className="px-4 py-4">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                    service.type === 'EPC' ? 'bg-purple-100 text-purple-700' : 
                                                    service.type === 'CAMINHÃO' ? 'bg-orange-100 text-orange-700' :
                                                    'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {service.type || 'N/A'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-4 text-xs text-gray-500">{service.applied_standards || '-'}</td>
                                            {/* NOVA COLUNA DADOS */}
                                            <td className="px-4 py-4 text-xs text-gray-500">{service.ansi_standards || '-'}</td>
                                            <td className="px-4 py-4 text-xs text-gray-500">{service.test_performed || '-'}</td>

                                            <td className="px-4 py-4 text-center">
                                                <span className="px-2.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-xs font-bold whitespace-nowrap">
                                                    {service.validity_months} meses
                                                </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="flex justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {canEdit && (
                                                        <button 
                                                            onClick={() => handleOpenModal(service)}
                                                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                                                            title="Editar"
                                                        >
                                                            <Edit size={16} />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button 
                                                            onClick={() => handleDelete(service.id)}
                                                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                                                            title="Excluir"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 text-xs text-gray-500">
                        Total de registros: {filteredServices.length}
                    </div>
                </div>
            )}

            {/* Modal de Cadastro/Edição */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-lg font-bold text-gray-800">
                                {editingService ? 'Editar Serviço' : 'Novo Serviço'}
                            </h2>
                            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSave} className="flex-1 overflow-y-auto p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Descrição do Serviço <span className="text-red-500">*</span></label>
                                <input 
                                    type="text" 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                                    value={formData.description}
                                    onChange={e => setFormData({...formData, description: e.target.value})}
                                    required
                                    placeholder="Ex: Luva Isolante de Borracha Classe 00"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
                                    <select 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
                                        value={formData.type}
                                        onChange={e => setFormData({...formData, type: e.target.value})}
                                    >
                                        <option value="EPI">EPI</option>
                                        <option value="EPC">EPC</option>
                                        <option value="CAMINHÃO">CAMINHÃO</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Validade (Meses) <span className="text-red-500">*</span></label>
                                    <input 
                                        type="number" 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.validity_months}
                                        onChange={e => setFormData({...formData, validity_months: parseInt(e.target.value) || ''})}
                                        required
                                        min="1"
                                        placeholder="Ex: 6"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Normas Aplicadas</label>
                                    <input 
                                        type="text" 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.applied_standards}
                                        onChange={e => setFormData({...formData, applied_standards: e.target.value})}
                                        placeholder="Ex: NBR 10622"
                                    />
                                </div>
                                {/* NOVO INPUT: Normas ANSI */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Normas ANSI</label>
                                    <input 
                                        type="text" 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                        value={formData.ansi_standards}
                                        onChange={e => setFormData({...formData, ansi_standards: e.target.value})}
                                        placeholder="Ex: ASTM F496"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Ensaio Realizado</label>
                                <input 
                                    type="text" 
                                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    value={formData.test_performed}
                                    onChange={e => setFormData({...formData, test_performed: e.target.value})}
                                    placeholder="Ex: Inspeção Visual e Tensão Aplicada"
                                />
                                <p className="text-xs text-gray-500 mt-1">Descrição sucinta do teste.</p>
                            </div>
                        </form>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
                            <button 
                                type="button" 
                                onClick={handleCloseModal}
                                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 transition-colors shadow-sm"
                            >
                                Cancelar
                            </button>
                            <button 
                                type="button" 
                                onClick={handleSave} 
                                disabled={saving}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700 shadow-md disabled:bg-blue-400 transition-colors"
                            >
                                {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServiceTablePage;